import type { ProjectState } from "@livcg/schema";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { sendRaw } from "../shared/sendRaw.js";
import { type ExtraBadgeState, extraBadgeState } from "./extraTimeBadge.js";

/** Staged (not yet sent) added time — per slug, at the MODULE
 *  level. The ClockControl/Header stepper writes it, while the "Extra time" tile
 *  (in a different component tree) reads it: in one press the tile must both
 *  write the value and bring up the banner. React state cannot carry this —
 *  the components are not each other's ancestors. */
const drafts = new Map<string, number>();
const listeners = new Set<() => void>();

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function setDraft(slug: string, value: number): void {
  drafts.set(slug, value);
  for (const fn of listeners) fn();
}

/** The staged value (`fallback` if there is none — the value in the replicant). */
export function stagedExtraTime(slug: string, fallback: number): number {
  return drafts.get(slug) ?? fallback;
}

/** Added time: −/+ change only the LOCAL value, while the replicant is
 *  written when the badge ("+N") is pressed. Otherwise, with the strip on air,
 *  4, 3, 2 would also be visible on the way from 5→1.
 *
 *  If the replicant value changes from outside (another panel, a phase change)
 *  the staging is discarded — the last write wins. */
export function useStagedExtraTime(slug: string, state: ProjectState) {
  const live = state.match.additionalTime;
  const onAir = state.match.extraTimeOnAir;
  const draft = useSyncExternalStore(
    subscribe,
    () => drafts.get(slug) ?? live,
    () => live,
  );
  const lastLive = useRef(live);

  useEffect(() => {
    if (lastLive.current !== live) {
      lastLive.current = live;
      drafts.delete(slug);
      for (const fn of listeners) fn();
    }
  }, [live, slug]);

  const badge: ExtraBadgeState = extraBadgeState(draft, live, onAir);

  return {
    /** The value shown in the panel (staged). */
    draft,
    badge,
    onAir,
    /** Whether pressing the badge is worthwhile — whether there is an unsent change. */
    canSend: badge === "staged",
    dec: () => setDraft(slug, Math.max(0, draft - 1)),
    inc: () => setDraft(slug, Math.min(20, draft + 1)),
    /** Writes the value to the replicant (does NOT PUT the strip ON AIR). */
    send: () => {
      if (draft === live) return;
      void sendRaw("clock.setAdditional", { slug, additionalTime: draft });
    },
    /** The "strip on air" checkbox — turning it on/off without the banner. */
    setOnAir: (next: boolean) => void sendRaw("clock.setExtraOnAir", { slug, onAir: next }),
  };
}

/** Tooltip text by badge state — so it is the same in all three places. */
export function extraBadgeTitle(badge: ExtraBadgeState, draft: number): string {
  switch (badge) {
    case "staged":
      return draft === 0
        ? "Qiymatni nolga tushirish uchun bosing"
        : `+${draft} ni saqlash uchun bosing`;
    case "sent":
      return 'Saqlangan — efirga chiqarish uchun "Extra time" plitkasini bosing';
    case "live":
      return "Strip efirda";
    default:
      return "Qo'shilgan vaqt kiritilmagan";
  }
}
